import { useEffect, useState, lazy, Suspense } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getTenantIsShadow } from "store/userSlice";
import { api } from "helpers/axios";
import { useOutletContext, useParams } from "react-router-dom";
import { errorMessage } from "helpers/common";
import * as Sentry from "@sentry/react";
import SettingRemarksModal from "components/ui/SettingRemarksModal";
import $ from "jquery";
import { toast } from "helpers/toast";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import { getCalculationOverrideReadOnly, setCalculationOverrideReadOnly } from "store/calculationSettingsSlice";

export default function CalculationForm() {

  const dispatch = useDispatch();

  // initialize popover
  $(function () {
    $('[data-toggle="popover"]').popover({
      html: true,
      trigger: 'hover',
      placement: 'auto'

    })
  });

  const { activeCalculationSection, setActiveCalculationSection, helps } = useOutletContext();

  const { editableTranslate } = useTranslation();

  const tenantIsShadow = useSelector(getTenantIsShadow);
  const overrideReadOnly = useSelector(getCalculationOverrideReadOnly);
  const [error, setError] = useState(null);

  const [Form, setForm] = useState(null);
  const { id } = useParams();
  const [calculation, setCalculation] = useState({});
  const [calculationOptions, setCalculationOptions] = useState([]);

  const [settingRemarks, setSettingRemarks] = useState({});

  const handleSaveRemarks = (id, content) => {
    if (!tenantIsShadow) return;

    const newSettingRemarks = { ...settingRemarks, [id]: content };
    setSettingRemarks(newSettingRemarks);
  };

  const lazyWithRetry = (componentImport) =>
    lazy(async () => {
      const pageHasAlreadyBeenForceRefreshed = JSON.parse(
        window.localStorage.getItem('page-has-been-force-refreshed') || 'false'
      );

      try {
        const component = await componentImport();

        window.localStorage.setItem('page-has-been-force-refreshed', 'false');

        return component;
      } catch (error) {
        if (!pageHasAlreadyBeenForceRefreshed) {
          // Assuming that the user is not on the latest version of the application.
          // Let's refresh the page immediately.
          window.localStorage.setItem('page-has-been-force-refreshed', 'true');
          window.location.reload();
          return;
        }

        // The page has already been reloaded
        // Assuming that user is already using the latest version of the application.
        // Let's let the application crash and raise the error.
        throw error;
      }
    });

  useEffect(() => {
    fetchCalculations();
  }, []);

  useEffect(() => {
    dispatch(setCalculationOverrideReadOnly(false));
    return () => dispatch(setCalculationOverrideReadOnly(false));
  }, [dispatch]);

  const fetchCalculations = async () => {
    try {
      const { data, status } = await api.get(`/settings/calculation`);
      if (status === 200) {
        const options = [];
        data.data.forEach(d => {
          if (d.hash_id !== id) {
            options.push({
              value: d.hash_id,
              label: d.name
            });
          }
        });
        setCalculationOptions(options);

        if (id) {
          const found = data.data.filter(d => d.hash_id === id)[0] || {};
          setCalculation({ ...found });
          setSettingRemarks(found.setting_remarks);
        }

        setForm(lazyWithRetry(() => import('components/Forms/CalculationSettings/V1')));
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch calculation settings'));
      Sentry.captureException(err);
    }
  };

  const onSubmit = async (data) => {
    data.setting_remarks = settingRemarks;
    data.override_in_use = overrideReadOnly;
    try {
      const { status } = await api.post(`/settings/calculation`, data);
      if (status === 200) return true;
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(editableTranslate('form.error.conflicted'));
      }

      Sentry.captureException(err);
      throw errorMessage(err, 'save calculation setting', false);
    }
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          <h4 className="mb-3">{editableTranslate('global.menu.settings.calculation')}</h4>
          {
            calculation.in_use && !overrideReadOnly && <div className="alert alert-warning" role="alert">
              {editableTranslate('calculation_settings.notice.in_use')}
            </div>
          }
          {
            error && <div>
              <ErrorAlert error={error} onClose={() => setError(null)} />
            </div>
          }

          {
            !Form && <div className="text-center" data-testid="loading">
              <div className="spinner-border" role="status">
                <span className="sr-only">Wird geladen...</span>
              </div>
            </div>
          }

          {
            Form && <Suspense fallback={<div></div>}>
              <Form submit={onSubmit}
                calculation={calculation} calculationOptions={calculationOptions}
                overrideReadOnly={overrideReadOnly}
                activeCalculationSection={activeCalculationSection} setActiveCalculationSection={setActiveCalculationSection}
                helps={helps} settingRemarks={settingRemarks} />
            </Suspense>
          }
          {
            tenantIsShadow && <SettingRemarksModal settingRemarks={settingRemarks} handleSaveRemarks={handleSaveRemarks} />
          }
        </div>
      </div>
    </>
  );
}
